import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Maximize2, Sun, Moon, MonitorPlay } from 'lucide-react';
import { tmdbApi } from '../api/tmdb';

// Provider URL builders
const buildVidsrcUrl = (tmdbId, mediaType = 'movie', season, episode) => {
  const isTV = mediaType === 'tv';
  if (isTV) {
    const base = `https://vidsrc.me/embed/tv?tmdb=${tmdbId}`;
    if (season && episode) return `${base}&season=${season}&episode=${episode}`;
    return base;
  }
  return `https://vidsrc.me/embed/movie?tmdb=${tmdbId}`;
};

const build2EmbedUrl = (tmdbId, mediaType = 'movie', season, episode) => {
  const isTV = mediaType === 'tv';
  if (isTV) {
    const base = `https://www.2embed.cc/embedtv/${tmdbId}`;
    if (season && episode) return `${base}&s=${season}&e=${episode}`;
    return base;
  }
  return `https://www.2embed.cc/embed/${tmdbId}`;
};

const PROVIDERS = [
  { key: 'vidsrc', label: 'VidSrc', build: buildVidsrcUrl },
  { key: '2embed', label: '2Embed', build: build2EmbedUrl },
];

const PlayerModal = ({
  tmdbId,
  mediaType = 'movie',
  title = 'Now Playing',
  initialSeason,
  initialEpisode,
  fallbackSource, // if provided, overrides constructed embed
  onClose,
}) => {
  const isTV = mediaType === 'tv';
  const [overlayDark, setOverlayDark] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState(initialSeason || 1);
  const [selectedEpisode, setSelectedEpisode] = useState(initialEpisode || 1);
  const [seasonData, setSeasonData] = useState(null);
  const [tvDetails, setTvDetails] = useState(null);
  const playerWrapperRef = useRef(null);
  const [iframeKey, setIframeKey] = useState(0);
  // If a fallback source (full URL) is provided, use it initially; once user interacts, switch to constructed URL
  const [useConstructedSrc, setUseConstructedSrc] = useState(!fallbackSource);
  const [providerKey, setProviderKey] = useState('vidsrc');
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const [iframeInteractive, setIframeInteractive] = useState(false);

  const addAutoplay = (url) => {
    if (!shouldAutoplay) return url;
    const params = ['autoplay=1','autoPlay=1','muted=1','mute=1','playsinline=1','controls=0'];
    const suffix = params.join('&');
    return url.includes('?') ? `${url}&${suffix}` : `${url}?${suffix}`;
  };

  const src = useMemo(() => {
    if (!isPlaying) return 'about:blank';
    const provider = PROVIDERS.find(p => p.key === providerKey) || PROVIDERS[0];
    if (useConstructedSrc) {
      return addAutoplay(provider.build(tmdbId, mediaType, selectedSeason, selectedEpisode));
    }
    return addAutoplay(fallbackSource || provider.build(tmdbId, mediaType, selectedSeason, selectedEpisode));
  }, [fallbackSource, tmdbId, mediaType, selectedSeason, selectedEpisode, isPlaying, useConstructedSrc, providerKey, shouldAutoplay]);

  // Derived display title that updates with season/episode
  const displayedTitle = useMemo(() => {
    if (!isTV) return title;
    const base = (title || '').split('• S')[0]?.trim() || title || 'Now Playing';
    const ep = seasonData?.episodes?.find((e) => e.episode_number === selectedEpisode);
    const epSuffix = ep?.name ? ` — ${ep.name}` : '';
    return `${base} • S${selectedSeason}E${selectedEpisode}${epSuffix}`;
  }, [isTV, title, seasonData, selectedSeason, selectedEpisode]);

  useEffect(() => {
    let ignore = false;
    const fetchSeason = async () => {
      if (!isTV || !tmdbId || !selectedSeason) return;
      try {
        const data = await tmdbApi.getTVSeason(tmdbId, selectedSeason);
        if (!ignore) setSeasonData(data);
      } catch (_) {
        if (!ignore) setSeasonData(null);
      }
    };
    fetchSeason();
    return () => {
      ignore = true;
    };
  }, [isTV, tmdbId, selectedSeason]);

  // Fetch TV details (for accurate seasons list)
  useEffect(() => {
    let ignore = false;
    const fetchDetails = async () => {
      if (!isTV || !tmdbId) return;
      try {
        const details = await tmdbApi.getTVDetails(tmdbId);
        if (!ignore) setTvDetails(details);
      } catch (_) {
        if (!ignore) setTvDetails(null);
      }
    };
    fetchDetails();
    return () => { ignore = true; };
  }, [isTV, tmdbId]);

  const seasonsList = useMemo(() => {
    const list = (tvDetails?.seasons || []).filter((s) => s.season_number > 0);
    if (list.length > 0) return list;
    // Fallback if details not loaded yet
    return Array.from({ length: Math.max(selectedSeason, 1) }).map((_, i) => ({ season_number: i + 1, id: `${tmdbId}-S${i+1}` }));
  }, [tvDetails, selectedSeason, tmdbId]);


  const enterFullscreen = () => {
    const wrapper = playerWrapperRef.current;
    if (!wrapper) return;
    const iframe = wrapper.querySelector('iframe');
    const target = iframe || wrapper;
    const rfs = target.requestFullscreen || target.webkitRequestFullscreen || target.mozRequestFullScreen || target.msRequestFullscreen;
    if (rfs) rfs.call(target);
  };

  const reloadPlayer = () => setIframeKey(k => k + 1);

  const handlePlay = () => {
    // Do not reload to avoid provider refresh loop; just allow user interaction
    setUseConstructedSrc(true);
    setShouldAutoplay(true);
    setIframeInteractive(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 z-[200] ${overlayDark ? 'bg-black/90' : 'bg-black/70'} backdrop-blur-sm flex items-center justify-center`}
      >
        <motion.div
          initial={{ scale: 0.98, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.98, opacity: 0 }}
          className="relative w-[95%] max-w-[1400px] min-h-[80vh] bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          {/* TV layout: left list, right player; Movie: full player */}
          {isTV ? (
            <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] h-full">
              {/* Left: Seasons and Episodes */}
              <div className="border-b md:border-b-0 md:border-r border-white/15 p-4 md:p-6 overflow-y-auto">
                <h3 className="text-white font-semibold mb-3">Seasons</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {seasonsList.map((s) => (
                    <button
                      key={s.id || s.season_number}
                      onClick={() => { setSelectedSeason(s.season_number); setSelectedEpisode(1); setUseConstructedSrc(true); setIframeKey((k)=>k+1); }}
                      className={`px-3 py-1 rounded-lg border ${selectedSeason === s.season_number ? 'bg-white/20 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/15'}`}
                    >
                      S{s.season_number}
                    </button>
                  ))}
                </div>
                <h4 className="text-white/90 font-semibold mb-2 flex items-center gap-2"><MonitorPlay size={16}/> Episodes</h4>
                <div className="space-y-2 max-h-[50vh] pe-2 overflow-y-auto">
                  {(seasonData?.episodes || []).map((ep) => (
                    <button
                      key={`${selectedSeason}-${ep.episode_number}`}
                      onClick={() => { setSelectedEpisode(ep.episode_number); setUseConstructedSrc(true); setIframeKey((k)=>k+1); }}
                      className={`w-full text-left p-3 rounded-xl border ${selectedEpisode === ep.episode_number ? 'bg-white/20 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/15'}`}
                    >
                      <div className="text-white text-sm font-medium truncate">E{ep.episode_number} • {ep.name || 'Episode'}</div>
                      {ep.overview && <div className="text-white/60 text-xs line-clamp-2">{ep.overview}</div>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right: Player */}
              <div className="p-4 md:p-6 flex flex-col">
                <div className="text-white/90 font-semibold mb-3 line-clamp-2">{displayedTitle}</div>
                {/* Provider selector */}
                <div className="mb-3 flex flex-wrap gap-2">
                  {PROVIDERS.map(p => (
                    <button
                      key={p.key}
                      onClick={() => { setProviderKey(p.key); setUseConstructedSrc(true); reloadPlayer(); }}
                      className={`px-3 py-1 rounded-xl border text-sm ${providerKey === p.key ? 'bg-white/20 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/15'}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div ref={playerWrapperRef} className="relative w-full aspect-video rounded-xl overflow-hidden border border-white/20 bg-black">
                  <iframe
                    key={iframeKey + ':' + src}
                    src={src}
                    className={`absolute inset-0 w-full h-full ${iframeInteractive ? '' : 'pointer-events-none'}`}
                    allowFullScreen
                    referrerPolicy="no-referrer"
                    allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                    title={`Player`}
                  />
                  {/* Custom overlay controls for iframe */}
                  {/* <div className="pointer-events-none absolute bottom-3 right-3 flex items-center gap-2">
                    <button onClick={() => setOverlayDark((d) => !d)} className="pointer-events-auto px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-sm">{overlayDark ? 'Light On' : 'Light Off'}</button>
                    <button onClick={enterFullscreen} className="pointer-events-auto px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-sm">Fullscreen</button>
                    <button onClick={reloadPlayer} className="pointer-events-auto px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-sm">Reload</button>
                  </div> */}
                </div>
                {/* Controls */}
                <div className="mt-4 flex items-center gap-3">
                  <button onClick={handlePlay} className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white inline-flex items-center gap-2">
                    <Play size={16}/> Play
                  </button>
                  <button onClick={() => setOverlayDark((d) => !d)} className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white/90 inline-flex items-center gap-2">
                    {overlayDark ? <Sun size={16}/> : <Moon size={16}/>} Light {overlayDark ? 'On' : 'Off'}
                  </button>
                  <button onClick={enterFullscreen} className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white/90 inline-flex items-center gap-2">
                    <Maximize2 size={16}/> Fullscreen
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-6 flex flex-col h-full">
              <div className="text-white/90 font-semibold mb-3 line-clamp-2">{displayedTitle}</div>
              {/* Provider selector */}
              <div className="mb-3 flex flex-wrap gap-2">
                {PROVIDERS.map(p => (
                  <button
                    key={p.key}
                    onClick={() => { setProviderKey(p.key); setUseConstructedSrc(true); reloadPlayer(); }}
                    className={`px-3 py-1 rounded-xl border text-sm ${providerKey === p.key ? 'bg-white/20 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/15'}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div ref={playerWrapperRef} className="relative w-full flex-1 min-h-[50vh] md:min-h-[60vh] rounded-xl overflow-hidden border border-white/20 bg-black">
                <iframe
                  key={iframeKey + ':' + src}
                  src={src}
                  className={`absolute inset-0 w-full h-full ${iframeInteractive ? '' : 'pointer-events-none'}`}
                  allowFullScreen
                  referrerPolicy="no-referrer"
                  allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                  title={`Player`}
                />
                {/* Custom overlay controls for iframe */}
                {/* <div className="pointer-events-none absolute bottom-3 right-3 flex items-center gap-2">
                  <button onClick={() => setOverlayDark((d) => !d)} className="pointer-events-auto px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-sm">{overlayDark ? 'Light On' : 'Light Off'}</button>
                  <button onClick={enterFullscreen} className="pointer-events-auto px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-sm">Fullscreen</button>
                  <button onClick={reloadPlayer} className="pointer-events-auto px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-sm">Reload</button>
                </div> */}
              </div>
              {/* Movie controls */}
              <div className="mt-4 flex items-center gap-3">
                <button onClick={handlePlay} className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white inline-flex items-center gap-2">
                  <Play size={16}/> Play
                </button>
                <button onClick={() => setOverlayDark((d) => !d)} className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white/90 inline-flex items-center gap-2">
                  {overlayDark ? <Sun size={16}/> : <Moon size={16}/>} Light {overlayDark ? 'On' : 'Off'}
                </button>
                <button onClick={enterFullscreen} className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white/90 inline-flex items-center gap-2">
                  <Maximize2 size={16}/> Fullscreen
                </button>
               
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PlayerModal;


